import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
import { AnimatePresence } from "framer-motion";
import PageTransition from "@/components/PageTransition";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ViewAsProvider } from "@/contexts/ViewAsContext";
import ViewAsBanner from "@/components/ViewAsBanner";
import BillingStatusBanner from "@/components/BillingStatusBanner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BottomTabBar from "@/components/BottomTabBar";
import AppBottomTabBar from "@/components/app/BottomTabBar";
import EngagementPopups from "@/components/EngagementPopups";
import ScrollToTop from "@/components/ScrollToTop";
import RequireAuth from "@/components/RequireAuth";
import RouteProgressBar from "@/components/RouteProgressBar";
import { ADMIN_LOGIN_PATH, ADMIN_PATH } from "@/lib/admin-access";
import Index from "./pages/Index";

// Legacy chat-bubble coach kept on disk for quick revert (unused).
const Pricing = lazy(() => import("./pages/Pricing"));
const Auth = lazy(() => import("./pages/Auth"));
const Dojo = lazy(() => import("./pages/Dojo"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Blog = lazy(() => import("./pages/Blog"));
const Reflections = lazy(() => import("./pages/Reflections"));
const Journal = lazy(() => import("./pages/Journal"));
const Philosophy = lazy(() => import("./pages/Philosophy"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const Sessions = lazy(() => import("./pages/Sessions"));
const Newsletter = lazy(() => import("./pages/Newsletter"));
const Chamber = lazy(() => import("./pages/Chamber"));
const CoachV2 = lazy(() => import("./pages/CoachV2"));

// Admin
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminGuard = lazy(() => import("./components/admin/AdminGuard"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminDiscounts = lazy(() => import("./pages/admin/AdminDiscounts"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminViewAs = lazy(() => import("./pages/admin/AdminViewAs"));
const AdminCMS = lazy(() => import("./pages/admin/AdminCMS"));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="container max-w-5xl px-4 md:px-6 py-8">
        <div className="space-y-6">
          <div className="h-8 w-48 bg-muted rounded-lg animate-pulse" />
          <div className="h-4 w-full max-w-md bg-muted rounded animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
          <div className="h-64 bg-muted rounded-2xl animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function AdminLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "hsl(0 0% 6%)" }}>
      <div className="w-8 h-8 border-2 border-[hsl(33,90%,47%)] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith(ADMIN_PATH);
  const isOnboarding = location.pathname === "/onboarding";
  const isApp = ["/chamber", "/journal", "/reflections", "/dashboard", "/coach", "/onboarding"].includes(location.pathname);

  if (isAdmin) {
    return (
      <Suspense fallback={<AdminLoader />}>
        <Routes>
          <Route path={ADMIN_LOGIN_PATH} element={<AdminLogin />} />
          <Route path={ADMIN_PATH} element={<AdminGuard />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Navigate to={`${ADMIN_PATH}/dashboard`} replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="transactions" element={<AdminOrders />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="view-as" element={<AdminViewAs />} />
              <Route path="cms" element={<AdminCMS />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <ViewAsBanner />
      <BillingStatusBanner />
      <a href="#main-content" className="skip-link">Skip to content</a>
      {!isApp && <Navbar />}

      {!isApp && !isOnboarding && <EngagementPopups />}
      <main id="main-content" className="min-h-screen pb-20 md:pb-0">
        <AnimatePresence mode="wait">
          <Suspense fallback={<PageLoader />} key={location.pathname}>
            <PageTransition key={location.pathname}>
              <Routes location={location}>
                <Route path="/" element={<Index />} />
                <Route path="/coach" element={<RequireAuth><CoachV2 /></RequireAuth>} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/chamber" element={<Chamber />} />
                <Route path="/dojo" element={<Dojo />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<Blog />} />
                <Route path="/reflections" element={<Reflections />} />
                <Route path="/philosophy" element={<Philosophy />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/sessions" element={<Sessions />} />
                <Route path="/newsletter" element={<Newsletter />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageTransition>
          </Suspense>
        </AnimatePresence>
      </main>
      {!isApp && !isOnboarding && <Footer />}
      {!isApp && !isOnboarding && <BottomTabBar />}
      {isApp && !isOnboarding && <AppBottomTabBar />}
    </>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ViewAsProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <RouteProgressBar />
              <ScrollToTop />
              <AppLayout />
            </BrowserRouter>
          </ViewAsProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
